'use client';

import { useEffect, useLayoutEffect, useRef, useState, useCallback, type ReactNode } from 'react';

/**
 * Wraps maintenance banner + site header. Hides on scroll down, shows again on scroll up.
 * Sets --site-top-chrome-h on <html> so <main> gets matching padding-top.
 */
export default function SiteTopChrome({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const accumulatedDelta = useRef(0);
  const raf = useRef<number | null>(null);

  /**
   * Always reserve the same padding-top for <main> as the chrome height, even when the bar is
   * visually hidden (translateY). If padding drops to 0 when hiding, document height changes and
   * scrollY jumps — especially near the bottom — which flips scroll direction and toggles the
   * header in a loop.
   */
  const updateChromeOffset = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    document.documentElement.style.setProperty('--site-top-chrome-h', `${h}px`);
  }, []);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    updateChromeOffset();
    const ro = new ResizeObserver(() => updateChromeOffset());
    ro.observe(el);
    window.addEventListener('resize', updateChromeOffset);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateChromeOffset);
    };
  }, [updateChromeOffset]);

  useEffect(() => {
    lastY.current = window.scrollY;
    accumulatedDelta.current = 0;

    const SHOW_THRESHOLD = 56;
    const HIDE_DELTA = 40;
    const SHOW_DELTA = 25;

    const onScroll = () => {
      if (raf.current != null) return;
      raf.current = window.requestAnimationFrame(() => {
        raf.current = null;
        const y = window.scrollY;
        const delta = y - lastY.current;
        lastY.current = y;

        if (y < SHOW_THRESHOLD) {
          setHidden(false);
          accumulatedDelta.current = 0;
          return;
        }

        if ((delta > 0 && accumulatedDelta.current < 0) ||
            (delta < 0 && accumulatedDelta.current > 0)) {
          accumulatedDelta.current = 0;
        }
        accumulatedDelta.current += delta;

        if (accumulatedDelta.current > HIDE_DELTA) {
          setHidden(true);
          accumulatedDelta.current = 0;
        } else if (accumulatedDelta.current < -SHOW_DELTA) {
          setHidden(false);
          accumulatedDelta.current = 0;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={`site-top-chrome${hidden ? ' site-top-chrome--hidden' : ''}`}
    >
      {children}
    </div>
  );
}
